/**
 * Extensión del cliente API existente con Session Token
 *
 * Este módulo extiende el cliente fetch existente con funcionalidad de session token:
 * - Inyecta headers de sesión (X-Session-Token, X-Fingerprint, X-Request-Nonce, X-Attestation, X-Timestamp)
 * - Genera Request Nonce único por petición
 * - Calcula Attestation HMAC-SHA256 para validación criptográfica
 * - Maneja errores de validación de fingerprint (403)
 * - Maneja errores de rate limiting (429)
 */

import { generateFingerprint } from '../utils/fingerprint'
import { generateRequestNonce } from '../utils/requestNonce'
import { generateAttestation, getClientSecret } from '../utils/attestation'

// Variable para almacenar el servicio de session token
let sessionTokenServiceInstance = null

/**
 * Inyecta el servicio de session token en el cliente API
 * Debe llamarse después de inicializar sessionTokenService
 */
export const injectSessionTokenService = (service) => {
  sessionTokenServiceInstance = service
  console.log('✅ Session token service injected into API client')
}

/**
 * Genera un ID único para la petición usando crypto API
 * @returns {string} UUID v4
 */
const generateRequestId = () => {
  // Usar crypto.randomUUID si está disponible (navegadores modernos)
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }

  // Fallback: generar UUID v4 simple
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

/**
 * Agrega headers de session token a un objeto de headers existente
 * Esta función debe ser llamada antes de hacer cualquier petición
 *
 * @param {Object} headers - Headers existentes
 * @returns {Promise<Object>} Headers con session token agregado
 */
export const addSessionTokenHeaders = async (headers = {}) => {
  try {
    const sessionHeaders = { ...headers }

    // Agregar Request ID único para trazabilidad
    sessionHeaders['X-Request-ID'] = generateRequestId()

    // Agregar fingerprint del navegador
    const fingerprint = await generateFingerprint()
    sessionHeaders['X-Fingerprint'] = fingerprint

    // Agregar timestamp actual (Unix timestamp en segundos)
    const timestamp = Math.floor(Date.now() / 1000).toString()
    sessionHeaders['X-Timestamp'] = timestamp

    // Generar Request Nonce único (one-time use)
    const nonce = generateRequestNonce()
    sessionHeaders['X-Request-Nonce'] = nonce

    // Esperar a que el token esté disponible
    if (sessionTokenServiceInstance) {
      // Esperar hasta que el servicio esté inicializado
      await sessionTokenServiceInstance.waitForInitialization()

      const token = sessionTokenServiceInstance.getToken()
      if (token) {
        sessionHeaders['X-Session-Token'] = token

        // Generar Attestation HMAC-SHA256
        // Formato del mensaje: {sessionToken}:{timestamp}:{nonce}:{sessionToken}
        const clientSecret = getClientSecret()
        if (clientSecret) {
          try {
            const attestation = await generateAttestation(token, timestamp, nonce, clientSecret)
            sessionHeaders['X-Attestation'] = attestation
          } catch (attestationError) {
            console.error('[SessionToken] Error generating attestation:', attestationError)
            // Continuar sin attestation - el backend rechazará la petición
          }
        } else {
          console.error('[SessionToken] CLIENT_SECRET not configured - attestation cannot be generated')
          // Continuar sin attestation - el backend rechazará la petición
        }
      } else {
        console.warn('[SessionToken] Token not available after initialization')
      }
    } else {
      console.warn('[SessionToken] Service not injected yet')
    }

    return sessionHeaders
  } catch (error) {
    console.error('[SessionToken] Error adding headers:', error)
    return headers
  }
}

/**
 * Maneja errores relacionados con session token
 *
 * @param {Response} response - Respuesta de fetch
 * @returns {Promise<boolean>} true si se debe reintentar la petición
 */
export const handleSessionTokenError = async (response) => {
  // Error 403: Fingerprint mismatch o token inválido
  if (response.status === 403) {
    try {
      const data = await response.clone().json()
      const reason = data.reason || data.message

      // Fingerprint mismatch - solicitar nuevo token
      if (reason && (reason.includes('fingerprint') || reason.includes('token'))) {
        console.warn('[SessionToken] Validation failed, requesting new token...')

        if (sessionTokenServiceInstance) {
          try {
            // Solicitar nuevo token
            await sessionTokenServiceInstance.requestToken()
            console.log('[SessionToken] New token obtained, retry the request')
            return true // Indica que se debe reintentar
          } catch (refreshError) {
            console.error('[SessionToken] Failed to refresh token:', refreshError)
          }
        }
      }
    } catch (e) {
      // Si no se puede parsear el JSON, continuar
    }
  }

  // Error 429: Rate limit exceeded
  if (response.status === 429) {
    const retryAfter = response.headers.get('retry-after') || '60'
    const retryAfterSeconds = parseInt(retryAfter, 10)

    console.warn(`[SessionToken] Rate limit exceeded. Retry after ${retryAfterSeconds}s`)

    // Emitir evento para notificar al usuario
    if (window.dispatchEvent) {
      window.dispatchEvent(
        new CustomEvent('rate-limit-exceeded', {
          detail: {
            retryAfter: retryAfterSeconds,
            message: 'Has realizado demasiadas peticiones. Por favor, espera un momento.',
          },
        })
      )
    }
  }

  return false
}

/**
 * Wrapper para fetch que agrega automáticamente los headers de session token
 * Puede ser usado como reemplazo directo de fetch
 *
 * @param {string|Request} input - URL o Request object
 * @param {Object} init - Opciones de fetch
 * @returns {Promise<Response>} Respuesta de fetch
 */
export const fetchWithSessionToken = async (input, init = {}) => {
  try {
    // Agregar headers de session token
    const headers = await addSessionTokenHeaders(init.headers || {})

    // Hacer la petición
    const response = await fetch(input, {
      ...init,
      headers,
    })

    // Manejar errores de session token
    if (!response.ok) {
      const shouldRetry = await handleSessionTokenError(response)

      // Si es 403 y se obtuvo un nuevo token, reintentar
      if (shouldRetry && response.status === 403) {
        const retryHeaders = await addSessionTokenHeaders(init.headers || {})
        return fetch(input, {
          ...init,
          headers: retryHeaders,
        })
      }
    }

    return response
  } catch (error) {
    console.error('[SessionToken] Fetch error:', error)
    throw error
  }
}

// Exportar el servicio para acceso directo si es necesario
export { sessionTokenServiceInstance }
